import { AdapterClient } from "@formant/core/client/AdapterClient";
import { defined } from "@formant/core/common/defined";
import { DefinedEntity } from "@formant/core/model/DefinedEntity";
import { IAdapter } from "@formant/core/model/IAdapter";
import { Uuid } from "@formant/core/model/Uuid";

import { IAdapterStore } from "./IAdapterStore";
import { IAuthStore } from "./IAuthStore";
import { StoreCache } from "./StoreCache";

export class AdapterStore implements IAdapterStore {
    private listCache = new StoreCache<undefined, DefinedEntity<IAdapter>[]>();
    private cache = new StoreCache<Uuid, DefinedEntity<IAdapter> | false>();

    constructor(
        private adapterClient: AdapterClient,
        private authStore: IAuthStore,
    ) {}

    public listAll(): DefinedEntity<IAdapter>[] | undefined {
        return this.listCache.get(
            undefined,
            async () => await this.fetchList(),
        );
    }

    public get(id: Uuid): DefinedEntity<IAdapter> | false | undefined {
        return this.cache.get(
            id,
            async () => (await this.fetchGet(id)) || false,
        );
    }

    public async create(adapter: IAdapter): Promise<DefinedEntity<IAdapter>> {
        const created = await this.adapterClient.create(
            await this.authStore.token(),
            adapter,
        );
        this.cache.set(defined(created.id), created);
        this.listCache.clear();
        return created;
    }

    public async update(
        id: Uuid,
        adapter: Partial<IAdapter>,
    ): Promise<DefinedEntity<IAdapter>> {
        const updated = await this.adapterClient.update(
            await this.authStore.token(),
            id,
            adapter,
        );
        this.cache.set(id, updated);
        this.listCache.clear();
        return updated;
    }

    public save(adapter: IAdapter): Promise<DefinedEntity<IAdapter>> {
        const { id } = adapter;
        if (id) {
            return this.update(id, adapter);
        } else {
            return this.create(adapter);
        }
    }

    public async delete(id: Uuid): Promise<void> {
        this.cache.set(id, false);
        await this.adapterClient.delete(await this.authStore.token(), id);
        await this.fetchList();
    }

    async fetchGet(id: Uuid): Promise<DefinedEntity<IAdapter> | false> {
        const adapter = await this.adapterClient.get(
            await this.authStore.token(),
            id,
        );
        const value = adapter || false;
        this.cache.set(id, value);
        return value;
    }

    private async fetchList(): Promise<DefinedEntity<IAdapter>[]> {
        const value = await this.adapterClient.list(
            await this.authStore.token(),
        );
        value.forEach(_ => this.cache.set(defined(_.id), _));
        this.listCache.set(undefined, value);
        return value;
    }
}
